import { AmountProps } from '@razorpay/blade/components';
import { actionStatusesMap } from 'components/RunPayroll/ComplianceActions/data';
import { dateFormats, getFormattedDateValue } from 'utils/Dates';
import { GetComplianceActionsSchemaContract } from 'schemas/GetComplianceActionsSchema';

export const getDataForPtDetailsView = (
  action: GetComplianceActionsSchemaContract['actions']['pay-pt'][string],
  payrollMonth?: string,
) => {
  const isPaymentOverdue = action.meta.is_over_due;

  const isPaymentDone =
    action.status === actionStatusesMap.inProgress ||
    action.status === actionStatusesMap.success ||
    action.status === actionStatusesMap.paidManually ||
    action.status === actionStatusesMap.refunded;

  const payrollMonthFormatted = getFormattedDateValue({
    date: payrollMonth,
    formatString: dateFormats.monthYear,
    filler: '',
  });

  const amountCurrency = (
    action.meta.amount?.[0]?.currency || 'inr'
  ).toUpperCase() as AmountProps['currency'];

  const paymentAmount = Number(action.meta.amount?.[0]?.value || 0);

  const isFailedPayment =
    action.status === actionStatusesMap.failed || action.status === actionStatusesMap.returnFailed;

  const shouldShowPayNowButton =
    action.status === actionStatusesMap.returnFailed || (!isPaymentOverdue && !isPaymentDone);

  const shouldShowMarkAsPaidButton = action.meta.mark_as_paid;

  const shouldShowPaymentButtons =
    paymentAmount > 0 && (shouldShowPayNowButton || shouldShowMarkAsPaidButton);

  // Coverage is scoped to this state's PT, not to org-wide payroll. Uncovered =
  // employees whose payroll did not run: skipped, or not yet executed. A skipped
  // one counts, because no PT was deducted for them and if they are paid later
  // their PT is not covered by this payment and XPayroll makes no second one.
  //
  // Deliberately NOT `eligible - covered`: an executed employee with no PT due
  // gets no transaction at all (generatePtTransaction skips ptAmount <= 0), and
  // would otherwise be reported as a gap that no amount of payroll can close.
  const eligiblePtEmployees = action.meta.pt_eligible_employees;
  const skippedPtEmployees = action.meta.pt_skipped_employees ?? 0;
  const pendingPtEmployees = action.meta.pt_pending_employees;

  const uncoveredPtEmployees = (pendingPtEmployees ?? 0) + skippedPtEmployees;

  // Undefined means the backend has not sent coverage yet; stay conservative and
  // keep routing through the partially-processed confirmation.
  const isPayrollProcessedForAllEmployees =
    pendingPtEmployees === null || pendingPtEmployees === undefined
      ? false
      : uncoveredPtEmployees === 0;

  const shouldHidePayrollProgress =
    !eligiblePtEmployees ||
    action.meta.is_over_due ||
    isPayrollProcessedForAllEmployees ||
    action.meta.manual_payment_outside_payroll ||
    !(action.status === 'not_initiated' || action.status === 'failed');

  const shouldShowKaPTPortalDowntimeBanner = action.is_portal_down && shouldShowPayNowButton;

  return {
    isPaymentOverdue,
    payrollMonthFormatted,
    amountCurrency,
    paymentAmount,
    isFailedPayment,
    shouldShowPayNowButton,
    shouldShowMarkAsPaidButton,
    shouldShowPaymentButtons,
    shouldHidePayrollProgress,
    isPayrollProcessedForAllEmployees,
    shouldShowKaPTPortalDowntimeBanner,
  };
};
